import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import selectors from 'store/selectors';

// Custom components
import CustomListView from 'components/ListView/Listview.cmp';
import 'pages/Widgets/TaskListing/TaskListView/taskListSummary.style.css';
// AG Grid
import {
  defaultColDef,
  columns,
  getRowStyles,
} from 'pages/Widgets/TaskListing/TaskListView/constants';
import { SortChangedEvent } from 'ag-grid-community';

// Utils
import { useUpdateColumn } from 'utils/CustomHooks/useUpdateColumn';
import xeniaApi from 'api/index';
import { TData } from 'memfs/lib/volume';
import { TasksListSummaryCnt } from 'pages/Widgets/TaskListing/TaskListView/tasksListSummary.style';
import { WidgetHeading, WidgetLoaderCnt } from 'pages/Widgets/widgets.style';
import CircularProgress from '@mui/material/CircularProgress';
import TaskListWidgetEmptyState from 'pages/Widgets/TaskListing/TaskListView/EmptyRecords/TaskListWidgetEmptyState.cmp';
import { setDialog } from 'store/actions/dialogActions';
import EmployeeFilterDropdown from 'pages/Widgets/TaskFilters/EmployeeFilter/EmployeeFilter';
import { generateAssigneeDropdownData } from 'helper/task/dropdownData';
import useUsers from 'utils/CustomHooks/useUsers';
import useDateTime from 'utils/CustomHooks/useDateTime';
import moment from 'moment';
import DueTodayEmptyState from 'pages/Widgets/TaskListing/TaskListView/EmptyRecords/DueTodayEmptyState';
import AssigneeFilterDropdown from 'components/AssigneeFilter/AssigneeFilter';

const DueTodayTasksListSummary = () => {
  const { taskAssignees } = useUsers();
  const userProfile: any = useSelector(selectors.getUserProfile);
  const { id } = userProfile;
  const assigneeDropdownData = useMemo(() => {
    return generateAssigneeDropdownData(taskAssignees(), id);
  }, [taskAssignees, userProfile]);
  const { updateColumns } = useUpdateColumn(null);
  const categoryList = useSelector(selectors.getServiceAdmin);

  const [data, setData] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const { getTodayInTz } = useDateTime();
  const { taskColumns } = useSelector((state: any) => {
    return {
      taskColumns: state?.taskHotelData?.taskColumns,
    };
  });
  const handleClearFilter = () => {
    setSelectedAssignee([]);
    fetchTaskList();
  };
  const fetchTaskList = async (customFilters = {}) => {
    // Fetch Report data
    setLoading(true);
    const requestPayload = {
      dueFilter: {
        dates: {
          from: getTodayInTz().startOf('day').utc().format(),
          to: getTodayInTz().endOf('day').utc().format(),
        },
      },
      assignees: [id],
      ...customFilters,
    };
    const data = await xeniaApi.getTasksList(requestPayload);
    setData(data);
    setLoading(false);
  };
  const handleEmployeeSelect = (option) => {
    setSelectedAssignee(option);
    const assigneeIds = option.map((t) => t.id);
    const updatedFilters = {
      ...(assigneeIds.length > 0 && {
        assignees: assigneeIds,
      }),
    };
    fetchTaskList(updatedFilters);
  };

  const reloadCallBack = () => {
    const assigneeIds = selectedAssignee?.map((t) => t.id);
    const updatedFilters = {
      ...(assigneeIds.length > 0 && {
        assignees: assigneeIds,
      }),
    };
    fetchTaskList(updatedFilters);
  };
  const onRowClick = (id) => {
    dispatch(
      setDialog({
        open: true,
        dialogId: 'taskDetail',
        data: {
          successCB: reloadCallBack,
          taskId: id,
        },
      }),
    );
  };
  useEffect(() => {
    if (id) {
      const selectedDropdownOption = assigneeDropdownData.filter(
        (t) => t.id === id,
      );
      const assigneeIds = selectedDropdownOption.map((t) => t.id);

      setSelectedAssignee(selectedDropdownOption);
      fetchTaskList({ assignees: assigneeIds });
    }
  }, [id]);
  //Event will be called when a sort will be changed
  const onSortChange = (event: SortChangedEvent<TData>) => {
    updateColumns(event.columnApi.getColumnState());
  };

  const colConfig = useMemo(
    () =>
      columns({
        categoryList,
      }),
    [taskColumns],
  );

  //Hiding last 2 options for columns menu
  const getMainMenuItems = (params) => {
    const newOptions = params.defaultItems.slice(
      0,
      params.defaultItems.length - 3,
    );
    return newOptions;
  };

  const getRowHeight = useCallback((params) => {
    return 50;
  }, []);

  return (
    <TasksListSummaryCnt className={'taskListSummaryCnt'}>
      <WidgetHeading>
        Due Today
        <AssigneeFilterDropdown
          selected={selectedAssignee}
          onChangeCallback={(option) => handleEmployeeSelect(option)}
          onClearCallback={handleClearFilter}
        />
      </WidgetHeading>

      {loading && (
        <WidgetLoaderCnt>
          <CircularProgress />
        </WidgetLoaderCnt>
      )}
      {!loading && data?.length === 0 && <DueTodayEmptyState />}
      {!loading && data?.length > 0 && (
        <CustomListView
          rootProps={{
            style: {
              height: 400,
              boxShadow: 'none',
              flex: 1,
            },
          }}
          onRowClick={onRowClick}
          gridProps={{
            ref: (ref) => {
              globalThis.taskSummaryListGrid = ref;
            },
            headerHeight: 30,
            onSortChanged: onSortChange,
            rowData: data,
            suppressChangeDetection: true,
            defaultColDef: defaultColDef,
            columnDefs: colConfig,
            suppressCellFocus: true,
            getMainMenuItems: getMainMenuItems,
            getRowHeight: getRowHeight,
            getRowStyle: getRowStyles,
            suppressRowClickSelection: true,
            getRowId: (params) => params.data.id,
          }}
        />
      )}
    </TasksListSummaryCnt>
  );
};

export default DueTodayTasksListSummary;
